import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check } from "lucide-react";
import api, { currency } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("polling");
  const [order, setOrder] = useState(null);
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");

  useEffect(() => {
    if (orderId && !sessionId) {
      api.get(`/orders/${orderId}`).then((r) => { setStatus("success"); setOrder(r.data); }).catch(() => setStatus("error"));
      return;
    }
    if (!sessionId) return;
    let tries = 0;
    const poll = async () => {
      tries++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          if (data.order_id) {
            try { const o = await api.get(`/orders/${data.order_id}`); setOrder(o.data); } catch {}
          }
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") { setStatus("failed"); return; }
        if (tries < 12) setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch { setStatus("error"); }
    };
    poll();
  }, [sessionId, orderId]);

  return (
    <div className="max-w-[900px] mx-auto px-6 py-32 text-center" data-testid="payment-success">
      {status === "polling" && (
        <>
          <div className="w-14 h-14 border-2 border-emerald border-t-transparent rounded-full mx-auto animate-spin" />
          <div className="mt-8 font-serif text-3xl">Confirming your payment…</div>
        </>
      )}
      {status === "success" && (
        <>
          <div className="w-16 h-16 rounded-full bg-emerald text-ivory mx-auto flex items-center justify-center"><Check className="w-8 h-8" /></div>
          <div className="overline text-emerald mt-6 mb-2">Order confirmed</div>
          <h1 className="font-serif text-5xl tracking-tight">Thank you.</h1>
          {order && (
            <>
              <p className="mt-6 text-charcoal/70">Your order <b data-testid="order-number">#{order.order_number}</b> is on its way to being made.</p>
              <div className="mt-10 max-w-lg mx-auto bg-ivory/60 p-6 text-left">
                <div className="overline text-emerald mb-3">Summary</div>
                {order.items.map((i) => (
                  <div key={i.product_id} className="flex justify-between py-2 text-sm border-b divider-gold last:border-0">
                    <span>{i.name} × {i.quantity}</span><span>{currency(i.line_total)}</span>
                  </div>
                ))}
                <div className="flex justify-between mt-4 pt-4 border-t divider-gold font-serif text-lg"><span>Total</span><span>{currency(order.total)}</span></div>
              </div>
            </>
          )}
          <div className="mt-10 flex justify-center gap-6">
            <Link to="/account?tab=orders" className="overline text-emerald">View orders →</Link>
            <Link to="/shop" className="overline text-charcoal/60">Continue shopping</Link>
          </div>
        </>
      )}
      {(status === "failed" || status === "error") && (
        <>
          <div className="font-serif text-4xl">We couldn't confirm your payment.</div>
          <p className="mt-4 text-charcoal/70">If you were charged, please contact us — we'll sort it out immediately.</p>
          <Link to="/cart" className="mt-8 inline-block overline text-emerald">Return to cart</Link>
        </>
      )}
      {status === "timeout" && (
        <>
          <div className="font-serif text-3xl">Still processing…</div>
          <p className="mt-4 text-charcoal/70">Your bank may need another moment. Please refresh — or check the order in your account.</p>
        </>
      )}
    </div>
  );
}
